/**
 * Vue courante du détail d'une campagne : onglet actif et section du test.
 *
 * Existe pour que le PARENT (le wrapper de queries de chaque portail) sache ce
 * que la page va afficher, et puisse ne charger que ça. Sans ce hook partagé,
 * l'état vivait dans `CampagneDetailView` et le parent chargeait à l'aveugle
 * toutes les passations de la campagne, même en restant sur l'onglet Parcours.
 *
 * Les deux valeurs vivent dans l'URL (`?onglet=pami&section=matching`) : coller
 * le lien reproduit la vue, et parent comme enfant lisent la même source
 * réactive.
 */
import type { TestCampagne } from "@repo/results";
import { useFiltresUrl } from "@repo/ui";
import {
  estTest,
  SANS_SCORE_GLOBAL,
  SECTIONS_DU_TEST,
  type OngletCampagne,
  type SectionTest,
} from "./ongletsCampagne";

/**
 * Référence stable : recréée à chaque rendu, elle ferait boucler les mémos de
 * `useFiltresUrl`. `test` n'est plus écrit : il n'est lu que pour traduire les
 * liens d'avant la navigation par test (`?onglet=comparaison&test=pami`).
 */
const DEFAUTS: { onglet: string; section: string; test: string } = {
  onglet: "parcours",
  section: "resultats",
  test: "",
};

/** Anciens onglets par fonction → section du test. */
const SECTION_HERITEE: Record<string, SectionTest> = {
  comparaison: "resultats",
  matching: "matching",
  referentiel: "referentiel",
  referents: "referents",
};

export type VueCampagne = {
  /**
   * Onglet demandé par l'URL. Non validé contre la campagne : la vue retombe
   * sur Parcours si la campagne ne fait pas passer ce test.
   */
  onglet: OngletCampagne;
  /** Section du test, bornée aux sections que ce test peut porter. */
  section: SectionTest;
  choisirOnglet: (onglet: OngletCampagne) => void;
  choisirSection: (section: SectionTest) => void;
  /**
   * Les passations de ce test sont-elles nécessaires à ce qui est affiché ?
   *
   * Seules la Synthèse et les sections Résultats / Matching les consomment :
   * Parcours — l'onglet d'arrivée — Référentiel et Référents n'en ont pas besoin.
   */
  chargerEvaluations: (test: TestCampagne) => boolean;
};

function lireOnglet(onglet: string, test: string): OngletCampagne {
  if (onglet in SECTION_HERITEE) {
    if (test === "synthese") return "synthese";
    return estTest(test) ? test : "parcours";
  }
  if (onglet === "synthese" || estTest(onglet)) return onglet;
  return "parcours";
}

export function useVueCampagne(): VueCampagne {
  const [params, setParams] = useFiltresUrl(DEFAUTS);

  const onglet = lireOnglet(params.onglet, params.test);
  const demandee = (SECTION_HERITEE[params.onglet] ?? params.section) as SectionTest;
  const sectionsPossibles = estTest(onglet) ? SECTIONS_DU_TEST[onglet] : [];
  const section = sectionsPossibles.includes(demandee) ? demandee : "resultats";

  // Changer de test garde la section si le nouveau test la porte : passer du
  // matching PAMI au matching fit culturel compare la même chose. Sinon, on
  // repart sur ses Résultats.
  const choisirOnglet = (valeur: OngletCampagne) => {
    const garde = estTest(valeur) && SECTIONS_DU_TEST[valeur].includes(section);
    setParams({ onglet: valeur, section: garde ? section : "resultats", test: "" });
  };

  const chargerEvaluations = (test: TestCampagne) =>
    (onglet === "synthese" && !SANS_SCORE_GLOBAL.includes(test)) ||
    (onglet === test && (section === "resultats" || section === "matching"));

  return {
    onglet,
    section,
    choisirOnglet,
    // `onglet` réécrit aussi : sur un lien hérité, l'URL porte encore l'ancien
    // onglet par fonction, qui primerait sur la section choisie.
    choisirSection: (valeur) => setParams({ onglet, section: valeur, test: "" }),
    chargerEvaluations,
  };
}
